import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, AlertCircle, Database, Wallet } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useSession } from '../../lib/SessionContext';
import { t, formatMoney } from '../../i18n';
import {
  Card, Button, Input, Select, Field, Alert, Spinner, EmptyState, Table,
} from '../../components/ui';
import { inRange, isBlank, isPaymentAlias } from '../../lib/settingsForm';
import SettingsCard, { useSettingsCard, NumberWithUnit, GoButton } from './SettingsCard';
import MercadoPagoCard from './MercadoPagoCard';

/**
 * What the professional charges, and what their patients see about it.
 *
 * All of it is this professional's own: the particular fee; the coverages they
 * accept, with the copay and what the obra social pays; when each of those
 * obras sociales pays them (0037); the cancellation cutoff with its amount per
 * obra social (0052); whether patients see payments, and where to transfer
 * (0046, 0062); and the patients on an individually agreed price. It used to be
 * split between Mi perfil and a tab of Datos maestros, the lists everyone
 * shares, with the particular fee editable in both.
 *
 * Fields in a card with Guardar wait for it. Rows of a table save each value
 * when the field is left, as they always did, and the card says so.
 */

/** Inline editable amount; commits on blur or Enter. */
const FeeInput = ({ value, onSave }) => {
  const [draft, setDraft] = useState(String(value ?? 0));
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDraft(String(value ?? 0)); setDirty(false); }, [value]);

  const commit = () => {
    if (!dirty) return;
    const next = Number(draft) || 0;
    setDirty(false);
    if (next !== Number(value)) onSave(next);
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        min="0"
        value={draft}
        onChange={(event) => { setDraft(event.target.value); setDirty(true); }}
        onBlur={commit}
        onKeyDown={(event) => event.key === 'Enter' && commit()}
        className="w-32"
      />
      <span className="whitespace-nowrap text-xs text-slate-400">{formatMoney(Number(draft) || 0)}</span>
    </div>
  );
};

/**
 * Like FeeInput, but empty means "not set" rather than zero: a cancellation fee
 * of zero charges nothing, while a missing one falls back to the private fee.
 * Saves null when emptied.
 */
const OptionalAmountInput = ({ value, onSave }) => {
  const shown = value == null ? '' : String(value);
  const [draft, setDraft] = useState(shown);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setDraft(value == null ? '' : String(value)); setDirty(false); }, [value]);

  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    if (draft.trim() === '') {
      if (value != null) onSave(null);
      return;
    }
    const next = Number(draft);
    if (Number.isNaN(next) || next < 0) { setDraft(shown); return; }
    if (next !== Number(value)) onSave(next);
  };

  return (
    <Input
      type="number"
      min="0"
      className="w-36"
      placeholder="—"
      value={draft}
      onChange={(event) => { setDraft(event.target.value); setDirty(true); }}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
    />
  );
};

/** What the last change to a table's rows did: saved, or why not. */
const useRowMessage = () => {
  const [message, setMessage] = useState(null);
  const show = useCallback((error, text) => {
    if (error) setMessage({ tone: 'danger', text: error.message });
    else setMessage(text ? { tone: 'success', text } : null);
  }, []);
  return [message, show];
};

const RowMessage = ({ message }) => (message ? <Alert tone={message.tone}>{message.text}</Alert> : null);

const ChargesTab = () => {
  const { profile, therapistId, refresh } = useSession();
  // Null until the first load. Later loads swap the rows in place: a spinner
  // then would unmount the cards and lose what is typed in them.
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState('');
  const [coverageMessage, showCoverageMessage] = useRowMessage();
  const [termMessage, showTermMessage] = useRowMessage();
  const [feeMessage, showFeeMessage] = useRowMessage();
  const [agreedMessage, showAgreedMessage] = useRowMessage();

  const privateFee = useSettingsCard('private-fee', { default_private_fee: profile.default_private_fee ?? 0 });
  const cancellation = useSettingsCard('cancellation-hours', {
    cancellation_notice_hours: profile.cancellation_notice_hours ?? '',
  });
  const payments = useSettingsCard('patient-payments', {
    shows_balance_to_patients: profile.shows_balance_to_patients ?? false,
    payment_alias: profile.payment_alias ?? '',
    payment_holder: profile.payment_holder ?? '',
  });

  const load = useCallback(async () => {
    const results = await Promise.all([
      supabase
        .from('therapist_insurance_plans')
        .select('*, insurance_plans(id,name,insurance_companies(id,name))')
        .eq('therapist_id', therapistId),
      supabase
        .from('insurance_plans')
        .select('id,name,insurance_companies(name)')
        .eq('is_active', true),
      // Patients on an individually agreed price. Discharged ones drop off:
      // the price was cleared when they left, so listing them would be noise.
      supabase
        .from('treatments')
        .select(`id, fee_override, billing_mode, insurance_plan_id, needs_invoice,
          insurance_plans(name, insurance_companies(name)), dependents(first_name, last_name)`)
        .eq('therapist_id', therapistId)
        .eq('status', 'approved')
        .not('fee_override', 'is', null),
      supabase
        .from('insurance_companies')
        .select('id, name, payment_term_kind, payment_term_value, payment_term_day')
        .eq('is_active', true)
        .order('name'),
      // This professional's own payment terms. Where they have not set one,
      // the obra social's default shows (migration 0037).
      supabase
        .from('therapist_payment_terms')
        .select('company_id, kind, value, day')
        .eq('therapist_id', therapistId),
      // What this professional charges for a cancellation under each obra social (0052).
      supabase
        .from('therapist_cancellation_fees')
        .select('company_id, amount')
        .eq('therapist_id', therapistId),
    ]);
    const failed = results.find((result) => result.error);
    if (failed) { setLoadError(failed.error.message); return; }
    setLoadError(null);

    const [accepted, allPlans, agreed, companyRows, termRows, feeRows] = results;
    const ownTerms = Object.fromEntries((termRows.data ?? []).map((row) => [row.company_id, row]));
    setData({
      coverages: accepted.data ?? [],
      plans: allPlans.data ?? [],
      agreedPrices: (agreed.data ?? [])
        .filter((row) => row.dependents)
        .map(({ dependents: person, ...treatment }) => ({
          ...treatment, first_name: person.first_name, last_name: person.last_name,
        }))
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es')),
      // A professional's own term replaces the obra social's as a whole,
      // never column by column: their "30 días corridos" must not borrow the
      // company's day of the month.
      companies: (companyRows.data ?? []).map((company) => (ownTerms[company.id]
        ? {
          ...company,
          payment_term_kind: ownTerms[company.id].kind,
          payment_term_value: ownTerms[company.id].value,
          payment_term_day: ownTerms[company.id].day,
        }
        : company)),
      fees: Object.fromEntries((feeRows.data ?? []).map((row) => [row.company_id, row.amount])),
    });
  }, [therapistId]);

  useEffect(() => { load(); }, [load]);

  // Future unpaid Particular sessions are re-priced by a database trigger,
  // except for patients on an individually agreed value.
  const savePrivateFee = () => privateFee.save(async (typed) => {
    const value = Number(typed.default_private_fee);
    if (isBlank(typed.default_private_fee) || Number.isNaN(value) || value < 0) return t('charges.privateFeeInvalid');
    const { error } = await supabase.from('therapists').update({ default_private_fee: value }).eq('id', therapistId);
    if (error) return error.message;
    refresh();
    return null;
  }, t('charges.privateFeeSaved'));

  // One cutoff, charged in full (0052). Empty is no policy; the database
  // refuses anything outside 1–720 too.
  const saveCancellation = () => cancellation.save(async (typed) => {
    const empty = isBlank(typed.cancellation_notice_hours);
    if (!empty && !inRange(typed.cancellation_notice_hours, [1, 720])) return t('charges.cancellationNoticeInvalid');
    const { error } = await supabase.from('therapists')
      .update({ cancellation_notice_hours: empty ? null : Number(typed.cancellation_notice_hours) })
      .eq('id', therapistId);
    if (error) return error.message;
    refresh();
    return empty ? { saved: { cancellation_notice_hours: '' } } : null;
  }, t('charges.cancellationSaved'));

  // Where patients transfer (0062): an alias or a 22-digit CBU/CVU, and the
  // holder's name so they can check who they pay. The database refuses a
  // malformed one too.
  const savePayments = () => payments.save(async (typed) => {
    const alias = String(typed.payment_alias).trim();
    const holder = String(typed.payment_holder).trim();
    if (alias && !isPaymentAlias(alias)) return t('charges.paymentAliasInvalid');
    if (alias && holder.length < 2) return t('charges.paymentHolderRequired');
    const { error } = await supabase.from('therapists').update({
      shows_balance_to_patients: typed.shows_balance_to_patients,
      payment_alias: alias || null,
      payment_holder: alias ? holder : null,
    }).eq('id', therapistId);
    if (error) return error.message;
    refresh();
    return { saved: { payment_alias: alias, payment_holder: alias ? holder : '' } };
  }, t('charges.paymentsSaved'));

  const addCoverage = async (planId) => {
    const { error } = await supabase.from('therapist_insurance_plans').insert([{
      therapist_id: therapistId,
      insurance_plan_id: planId,
      patient_copay: 0,
      plan_reimbursement: 0,
    }]);
    if (error) { showCoverageMessage(error); return; }
    showCoverageMessage(null);
    setAdding('');
    load();
  };

  const updateFees = async (planId, patch) => {
    const { error } = await supabase
      .from('therapist_insurance_plans')
      .update(patch)
      .eq('therapist_id', therapistId)
      .eq('insurance_plan_id', planId);
    showCoverageMessage(error, t('charges.coveragesSaved'));
    load();
  };

  const removeCoverage = async (planId) => {
    const { error } = await supabase
      .from('therapist_insurance_plans')
      .delete()
      .eq('therapist_id', therapistId)
      .eq('insurance_plan_id', planId);
    showCoverageMessage(error);
    load();
  };

  const saveTerm = async (company, kind, value, day) => {
    // A fixed day needs one: the 1st until she says otherwise.
    const onDay = kind === 'day_of_month' ? Math.min(31, Math.max(1, Number(day) || 1)) : null;
    const { error } = await supabase.rpc('set_company_payment_term', {
      p_company_id: company.id,
      p_kind: kind,
      p_value: Number(value) || 0,
      p_day: onDay,
    });
    showTermMessage(error, t('charges.termSaved'));
    load();
  };

  // Applies to cancellations charged from now on; ones already charged keep
  // the amount they were given.
  const saveFee = async (companyId, amount) => {
    const { error } = await supabase.rpc('set_cancellation_fee', { p_company_id: companyId, p_amount: amount });
    showFeeMessage(error, t(amount == null ? 'charges.cancellationFeeCleared' : 'charges.cancellationFeeSaved'));
    load();
  };

  const clearAgreedPrice = async (patient) => {
    const name = `${patient.first_name} ${patient.last_name}`;
    if (!window.confirm(t('charges.confirmClearPrice', { name }))) return;
    // The rest of how this patient is charged stays as it is; only the agreed
    // value goes, and their future unpaid sessions fall back to the default.
    const { error } = await supabase.rpc('update_treatment_billing', {
      p_treatment_id: patient.id,
      p_billing_mode: patient.billing_mode,
      p_insurance_plan_id: patient.insurance_plan_id,
      p_fee_override: null,
      p_needs_invoice: patient.needs_invoice,
    });
    showAgreedMessage(error, t('charges.priceCleared', { name }));
    load();
  };

  if (!data) {
    if (!loadError) return <Spinner />;
    return (
      <Alert tone="danger" title={t('charges.loadFailed')}>
        <p>{loadError}</p>
        <Button variant="secondary" className="mt-3" onClick={load}>{t('common.retry')}</Button>
      </Alert>
    );
  }

  const acceptedIds = new Set(data.coverages.map((row) => row.insurance_plan_id));
  const available = data.plans.filter((plan) => !acceptedIds.has(plan.id));
  // When an obra social pays and what a cancellation costs under it matter only
  // for the ones this professional attends, so the rest of the practice's list
  // stays out of both tables. A value set earlier for one they no longer attend
  // stays stored.
  const attendedIds = new Set(data.coverages.map((row) => row.insurance_plans?.insurance_companies?.id));
  const attended = data.companies.filter((company) => attendedIds.has(company.id));
  const noneAttended = <p className="text-sm text-slate-500">{t('charges.noAttendedCompanies')}</p>;

  return (
    <div className="space-y-4">
      {loadError && <Alert tone="danger">{loadError}</Alert>}

      <SettingsCard
        title={t('charges.privateFeeTitle')}
        subtitle={t('charges.privateFeeHint')}
        card={privateFee.card}
        onSave={savePrivateFee}
      >
        <Field label={t('charges.privateFeeLabel')}>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number" min="0" className="w-40"
              value={privateFee.form.default_private_fee}
              onChange={(event) => privateFee.change({ default_private_fee: event.target.value })}
            />
            <span className="text-xs text-slate-400">
              {formatMoney(Number(privateFee.form.default_private_fee) || 0)}
            </span>
          </div>
        </Field>
      </SettingsCard>

      <Card title={t('charges.coveragesTitle')} subtitle={t('charges.coveragesHint')}>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Select value={adding} onChange={(event) => setAdding(event.target.value)} className="min-w-56">
              <option value="">{t('charges.addCoverage')}</option>
              {available.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.insurance_companies?.name} · {plan.name}
                </option>
              ))}
            </Select>
            <Button disabled={!adding} onClick={() => addCoverage(adding)} title={t('charges.addCoverage')}>
              <Plus size={16} />
            </Button>
          </div>

          <RowMessage message={coverageMessage} />

          {data.coverages.length === 0 ? (
            <EmptyState icon={AlertCircle} title={t('charges.noCoverages')} description={t('charges.noCoveragesHint')} />
          ) : (
            <Table headers={[t('insurance.company'), t('insurance.patientCopay'), t('insurance.planReimbursement'), '']}>
              {data.coverages.map((row) => (
                <tr key={row.insurance_plan_id}>
                  <td className="px-3 py-2">
                    <p className="font-medium text-slate-900">{row.insurance_plans?.insurance_companies?.name}</p>
                    <p className="text-xs text-slate-500">{row.insurance_plans?.name}</p>
                  </td>
                  <td className="px-3 py-2">
                    <FeeInput
                      value={row.patient_copay}
                      onSave={(value) => updateFees(row.insurance_plan_id, { patient_copay: value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <FeeInput
                      value={row.plan_reimbursement}
                      onSave={(value) => updateFees(row.insurance_plan_id, { plan_reimbursement: value })}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => removeCoverage(row.insurance_plan_id)}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title={t('charges.stopAccepting')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </Table>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <p className="text-sm text-slate-600">{t('charges.missingCoverage')}</p>
            <GoButton to="/datos-maestros"><Database size={16} />{t('charges.goToMasterData')}</GoButton>
          </div>
        </div>
      </Card>

      {/* When each obra social pays. Drives whether a debt shows as overdue,
          and is knowledge the professional holds rather than admin. */}
      <Card title={t('charges.termsTitle')} subtitle={t('charges.termsHint')}>
        <div className="space-y-4">
          <RowMessage message={termMessage} />
          {attended.length === 0 ? noneAttended : (
            <Table headers={[t('insurance.company'), t('charges.termKind'), t('charges.termValue')]}>
              {attended.map((company) => (
                <tr key={company.id}>
                  <td className="px-3 py-2 font-medium text-slate-800">{company.name}</td>
                  <td className="px-3 py-2">
                    <Select
                      value={company.payment_term_kind}
                      className="min-w-52"
                      onChange={(event) => saveTerm(
                        company, event.target.value, company.payment_term_value, company.payment_term_day,
                      )}
                    >
                      <option value="end_of_month">{t('charges.termEndOfMonth')}</option>
                      <option value="start_of_month">{t('charges.termStartOfMonth')}</option>
                      <option value="days">{t('charges.termDays')}</option>
                      <option value="day_of_month">{t('charges.termDayOfMonth')}</option>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* A fixed day takes two numbers: the day it pays on, and
                          how many months after the session's own. */}
                      {company.payment_term_kind === 'day_of_month' && (
                        <>
                          <Input
                            type="number" min="1" max="31" className="w-20"
                            key={`day-${company.id}-${company.payment_term_day ?? 1}`}
                            defaultValue={company.payment_term_day ?? 1}
                            onBlur={(event) => {
                              const next = Number(event.target.value) || 1;
                              if (next !== company.payment_term_day) {
                                saveTerm(company, 'day_of_month', company.payment_term_value, next);
                              }
                            }}
                          />
                          <span className="text-xs text-slate-500">{t('charges.termDayUnit')}</span>
                        </>
                      )}
                      <Input
                        type="number" min="0" className="w-24"
                        key={`value-${company.id}-${company.payment_term_kind}`}
                        defaultValue={company.payment_term_value}
                        onBlur={(event) => {
                          const next = Number(event.target.value) || 0;
                          if (next !== company.payment_term_value) {
                            saveTerm(company, company.payment_term_kind, next, company.payment_term_day);
                          }
                        }}
                      />
                      <span className="text-xs text-slate-500">
                        {company.payment_term_kind === 'days' ? t('charges.termDaysUnit') : t('charges.termMonthsUnit')}
                      </span>
                    </div>
                    {company.payment_term_kind === 'day_of_month' && (
                      <p className="mt-1 text-xs text-slate-400">{t('charges.termDayOfMonthHint')}</p>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </Card>

      {/* The cutoff and what a charged cancellation costs under each obra
          social, together: they are one policy. */}
      <SettingsCard
        title={t('charges.cancellationTitle')}
        subtitle={t('charges.cancellationHint')}
        card={cancellation.card}
        onSave={saveCancellation}
        after={(
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">{t('charges.cancellationFeesTitle')}</h3>
              <p className="text-xs text-slate-500">{t('charges.cancellationFeesHint')}</p>
            </div>
            <RowMessage message={feeMessage} />
            {attended.length === 0 ? noneAttended : (
              <Table headers={[t('insurance.company'), t('charges.cancellationFee')]}>
                {attended.map((company) => (
                  <tr key={company.id}>
                    <td className="px-3 py-2 font-medium text-slate-800">{company.name}</td>
                    <td className="px-3 py-2">
                      <OptionalAmountInput
                        value={data.fees[company.id] ?? null}
                        onSave={(amount) => saveFee(company.id, amount)}
                      />
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        )}
      >
        <Field label={t('charges.cancellationNoticeHours')} hint={t('charges.cancellationNoticeHoursHint')}>
          <NumberWithUnit
            min="1" max="720" placeholder="—" unit={t('charges.hoursOfNotice')}
            value={cancellation.form.cancellation_notice_hours}
            onChange={(event) => cancellation.change({ cancellation_notice_hours: event.target.value })}
          />
        </Field>
      </SettingsCard>

      {/* Off until the professional turns it on: some prefer to talk about
          money in session rather than have the app say it (0046). */}
      <SettingsCard title={t('charges.paymentsTitle')} card={payments.card} onSave={savePayments}>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={payments.form.shows_balance_to_patients}
            onChange={(event) => payments.change({ shows_balance_to_patients: event.target.checked })}
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-slate-800">
              <Wallet size={15} />{t('charges.showsBalance')}
            </span>
            <span className="mt-0.5 block text-sm text-slate-500">{t('charges.showsBalanceHint')}</span>
          </span>
        </label>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-semibold text-slate-900">{t('charges.transferTitle')}</h3>
          <p className="mb-3 text-xs text-slate-500">{t('charges.transferHint')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('charges.paymentAlias')} hint={t('charges.paymentAliasHint')}>
              <Input
                value={payments.form.payment_alias}
                onChange={(event) => payments.change({ payment_alias: event.target.value })}
              />
            </Field>
            <Field label={t('charges.paymentHolder')}>
              <Input
                value={payments.form.payment_holder}
                onChange={(event) => payments.change({ payment_holder: event.target.value })}
              />
            </Field>
          </div>
          {!payments.form.shows_balance_to_patients && !isBlank(payments.form.payment_alias) && (
            <p className="mt-3 text-xs text-amber-700">{t('charges.paymentsNotShown')}</p>
          )}
        </div>
      </SettingsCard>

      {/* Beside where to transfer: the other way a patient pays (0074). */}
      <MercadoPagoCard />

      {/* Patients whose price was agreed individually, in one place. */}
      <Card title={t('charges.agreedPricesTitle')} subtitle={t('charges.agreedPricesHint')}>
        <div className="space-y-4">
          <RowMessage message={agreedMessage} />
          {data.agreedPrices.length === 0 ? (
            <EmptyState title={t('charges.noAgreedPrices')} />
          ) : (
            <Table headers={[t('appointments.patient'), t('insurance.coverage'), t('patients.agreedValue'), '']}>
              {data.agreedPrices.map((patient) => (
                <tr key={patient.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {patient.first_name} {patient.last_name}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {patient.insurance_plans
                      ? `${patient.insurance_plans.insurance_companies?.name} · ${patient.insurance_plans.name}`
                      : t('insurance.particular')}
                  </td>
                  <td className="px-3 py-2 font-medium">{formatMoney(patient.fee_override)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" onClick={() => clearAgreedPrice(patient)} title={t('charges.clearPrice')}>
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ChargesTab;
